/*
 * Search Drawer
 * -------------
 * Owns the global predictive-search drawer's lifecycle (rendered once
 * from layout/theme.liquid per DECISIONS D25):
 *   1. Open / close lifecycle (focus trap, scroll lock, Escape, overlay click,
 *      mobile menu-toggle close intercept, return focus to last trigger).
 *   2. Input forwarding — every keystroke dispatches a `search-drawer:query`
 *      CustomEvent on `document`. The Voyado autocomplete script (bound to
 *      [data-voyado-autocomplete]) listens, debounces and renders into the
 *      drawer's results region. This file no longer fetches predictive
 *      results directly (Voyado migration — see orch_VOYADO_SEARCH_PLP).
 *
 * Hooks: [data-search-drawer], [data-search-drawer-panel],
 *        [data-search-drawer-overlay], [data-search-drawer-input],
 *        [data-search-drawer-trigger], [data-search-drawer-close],
 *        [data-menu-toggle] (mobile close intercept).
 * Depends on: focus-trap.js (loaded globally in theme.liquid).
 */
(function () {
  'use strict';
  /* global createFocusTrap */

  const container = document.querySelector('[data-search-drawer]');
  if (!container) return;

  // JS-only cursor hint on triggers — opens an input-style drawer, so the
  // I-beam fits the JS behaviour. No-JS users keep the default link pointer.
  document.querySelectorAll('[data-search-drawer-trigger]').forEach(function (t) {
    t.classList.add('cursor-text');
  });

  const panel = container.querySelector('[data-search-drawer-panel]');
  const overlay = container.querySelector('[data-search-drawer-overlay]');
  const input = container.querySelector('[data-search-drawer-input]');
  const scroller = container.querySelector('[data-drawer-scroll]');

  const abortController = new AbortController();
  const signal = abortController.signal;

  let lastTrigger = null, trap = null, clearTimer = null;

  // Apply a state change to the panel without animating its translate transition
  // (coordinator tab-switch path): the panel jumps on/off-screen instantly while
  // only the CONTENT cross-fades via the host CSS. Mirrors mini-bag/wishlist. JS-only.
  function withNoSlide(fn) {
    var prev = panel.style.transition;
    panel.style.transition = 'none';
    fn();
    void panel.offsetWidth;                 // force reflow so the no-transition write commits
    requestAnimationFrame(function () { panel.style.transition = prev; });
  }

  // --- Open / close ------------------------------------------------------
  function isOpen() {
    return container.getAttribute('data-search-drawer-open') === 'true';
  }

  // Mirrors open state onto [data-menu-toggle] so the hamburger morphs
  // into the X used as the mobile close affordance.
  function syncTriggers(value) {
    document.querySelectorAll('[data-search-drawer-trigger]').forEach(function (t) {
      if (t.hasAttribute('aria-expanded')) t.setAttribute('aria-expanded', value);
    });
    const menuToggle = document.querySelector('[data-menu-toggle]');
    if (menuToggle) menuToggle.setAttribute('data-search-drawer-open', value);
    const header = document.querySelector('[data-header]');
    if (header) header.setAttribute('data-search-drawer-open', value);
  }

  // opts.tabSwitch (from the coordinator's mobile cross-fade) keeps aria-hidden/
  // inert + syncTriggers + the open-state flag ALWAYS, but skips scroll-lock,
  // focus-trap, input focus and the deferred clear so the panel just jumps
  // on-screen (via withNoSlide) while the host CSS cross-fades the body.
  function openDrawer(trigger, opts) {
    if (isOpen()) return;
    lastTrigger = trigger || null;
    syncTriggers('true');
    panel.removeAttribute('aria-hidden');
    panel.removeAttribute('inert');
    if (!opts || !opts.tabSwitch) {
      // Run a pending close-clear now (not just cancel) so stale results don't flash before typing.
      if (clearTimer) { clearTimeout(clearTimer); clearTimer = null; clearSearch(); }
      container.setAttribute('data-search-drawer-open', 'true');
      if (scroller) scroller.scrollTop = 0; // open from the top, resetting any prior scroll
      document.documentElement.setAttribute('data-scroll-lock', '');
      if (typeof createFocusTrap === 'function') {
        trap = trap || createFocusTrap(panel);
        trap.activate();
        trap.focusFirst();
      }
      // Land on the input, not the close button which precedes it in tab order.
      requestAnimationFrame(function () { input.focus(); });
    } else {
      withNoSlide(function () { container.setAttribute('data-search-drawer-open', 'true'); });
    }
  }

  function closeDrawer(opts) {
    if (!isOpen()) return;
    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('inert', '');
    syncTriggers('false');
    if (!opts || !opts.tabSwitch) {
      container.setAttribute('data-search-drawer-open', 'false');
      document.documentElement.removeAttribute('data-scroll-lock');
      if (trap) trap.deactivate();
      if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
      lastTrigger = null;
      // Reset the search to its empty state AFTER the close animation finishes (the
      // content fades 0–300ms; the panel slides out, delay 300 + duration 300, ending
      // at 600ms) so the reset isn't seen. Run early by openDrawer on a fast re-open.
      clearTimeout(clearTimer);
      clearTimer = setTimeout(function () { clearTimer = null; if (!isOpen()) clearSearch(); }, 600);
    } else {
      withNoSlide(function () { container.setAttribute('data-search-drawer-open', 'false'); });
    }
  }

  // --- Event delegation: triggers (open only), overlay, close, Escape ------
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-search-drawer-trigger]');
    if (trigger) {
      e.preventDefault();
      if (!isOpen()) openDrawer(trigger);   // own-icon re-click is a no-op while open (close via the X / overlay)
      return;
    }
    if (e.target.closest('[data-search-drawer-close]')) {
      e.preventDefault();
      closeDrawer();
    }
  }, { signal });

  // Mobile close routing: while open, capture-phase intercepts the
  // menu-toggle click so header.js doesn't also open the menu drawer.
  document.addEventListener('click', function (e) {
    if (!isOpen()) return;
    const menuBtn = e.target.closest('[data-menu-toggle]');
    if (!menuBtn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    closeDrawer();
  }, { signal, capture: true });

  overlay.addEventListener('click', function () {
    if (isOpen()) closeDrawer();
  }, { signal });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) {
      closeDrawer();
    }
  }, { signal });

  // --- Input forwarding to [data-voyado-autocomplete] -------------------
  // Each keystroke dispatches `search-drawer:query`; the host owns debounce +
  // fetch. The default region (popular/help + zero-term trending slots) shows
  // when empty; the results host shows when typing.
  const defaultRegion = container.querySelector('[data-search-drawer-default]');
  const resultsRegion = container.querySelector('[data-search-drawer-results]');
  input.addEventListener('input', function () {
    const typing = input.value.trim().length > 0;
    if (defaultRegion) defaultRegion.classList.toggle('hidden', typing);
    if (resultsRegion) resultsRegion.classList.toggle('hidden', !typing);
    document.dispatchEvent(new CustomEvent('search-drawer:query', {
      detail: { query: input.value },
      bubbles: true
    }));
  }, { signal });

  // Dismiss the on-screen keyboard on touch without leaving the drawer (it searches live): the search
  // icon button and the mobile keyboard's Search key blur the input. On a non-touch device there is no
  // keyboard to dismiss, so let the form submit natively (Enter → the full results page) and keep focus.
  const isTouch = function () { return window.matchMedia('(pointer: coarse)').matches; };
  const form = container.querySelector('[data-search-drawer-form]');
  if (form) form.addEventListener('submit', function (e) {
    if (!isTouch()) return;                 // desktop: native GET submit navigates to /search
    e.preventDefault();
    if (input) input.blur();
  }, { signal });
  const dismissBtn = container.querySelector('[data-search-drawer-dismiss]');
  if (dismissBtn) dismissBtn.addEventListener('click', function () { if (isTouch() && input) input.blur(); }, { signal });

  // Reset the drawer to its empty/default state — input cleared, default region
  // (popular/help + trending) shown, results + view-all hidden. Called on close.
  function clearSearch() {
    if (input) input.value = '';
    if (defaultRegion) defaultRegion.classList.remove('hidden');
    if (resultsRegion) resultsRegion.classList.add('hidden');
    const viewAll = container.querySelector('[data-search-drawer-view-all-wrap]');
    if (viewAll) viewAll.setAttribute('data-state', 'hidden');
    // Empty the host synchronously — the empty-q dispatch below re-renders async, so this is
    // what stops stale results flashing on a fast re-open.
    const acHost = container.querySelector('[data-voyado-autocomplete]');
    if (acHost) acHost.replaceChildren();
    document.dispatchEvent(new CustomEvent('search-drawer:query', { detail: { query: '' }, bubbles: true }));
  }

  // --- Public API — the drawer coordinator drives the mobile cross-fade -----
  window.broraSearchDrawer = { open: openDrawer, close: closeDrawer, isOpen: isOpen };

  // --- Cleanup on section:unload -----------------------------------------
  document.addEventListener('shopify:section:unload', function () {
    abortController.abort();
  }, { once: true });
})();
